import { useEffect, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'

const MATOMO_URL = process.env.REACT_APP_MATOMO_URL
const MATOMO_SITE_ID = process.env.REACT_APP_MATOMO_SITE_ID
const MATOMO_ENABLED = process.env.REACT_APP_MATOMO_ENABLED !== 'false'
const APP_ENV = process.env.REACT_APP_ENV || 'development'
const UC_SERVICE_NAME = 'Matomo (self hosted)'

function getMatomoConsent() {
  try {
    const services = globalThis.UC_UI?.getServicesBaseInfo?.() ?? []
    const service = services.find((s) => s.name === UC_SERVICE_NAME)
    return service?.consent?.status ?? false
  } catch {
    return false
  }
}

function pushOrReplace(paq, commands) {
  for (const [key, ...args] of commands) {
    if (Array.isArray(paq)) {
      const existingIndex = paq.findIndex(
        (cmd) => Array.isArray(cmd) && cmd[0] === key,
      )
      if (existingIndex === -1) {
        paq.push([key, ...args])
      } else {
        paq[existingIndex] = [key, ...args]
      }
    } else {
      paq.push([key, ...args])
    }
  }
}

function MatomoTracker() {
  const location = useLocation()
  const consentGiven = useRef(false)

  const trackPageView = useCallback(() => {
    if (!globalThis._paq || !consentGiven.current) return
    globalThis._paq.push(
      ['setCustomUrl', globalThis.location.pathname],
      ['setDocumentTitle', document.title],
      ['trackPageView'],
    )
  }, [])

  const applyConsent = useCallback(() => {
    if (!globalThis._paq) return
    const hasConsent = getMatomoConsent()

    if (hasConsent && !consentGiven.current) {
      globalThis._paq.push(['setConsentGiven'])
      consentGiven.current = true

      let attempts = 0
      const waitAndTrack = () => {
        if (globalThis.Matomo) {
          trackPageView()
        } else if (attempts < 20) {
          attempts++
          setTimeout(waitAndTrack, 100)
        } else {
          console.warn(`${UC_SERVICE_NAME} failed to load after 2s`)
        }
      }
      waitAndTrack()
    } else if (!hasConsent && consentGiven.current) {
      globalThis._paq.push(['forgetConsentGiven'], ['forgetUserOptOut'])
      consentGiven.current = false
    }
  }, [trackPageView])

  useEffect(() => {
    if (!MATOMO_ENABLED || !MATOMO_URL || !MATOMO_SITE_ID) {
      console.warn(`${UC_SERVICE_NAME} tracking disabled or not configured`)
      return
    }

    globalThis._paq = globalThis._paq || []

    pushOrReplace(globalThis._paq, [
      ['requireConsent'],
      ['setTrackerUrl', `${MATOMO_URL}/matomo.php`],
      ['setSiteId', MATOMO_SITE_ID],
      ['setCustomDimension', 1, APP_ENV],
    ])

    const existing = document.querySelector(
      `script[data-usercentrics="${UC_SERVICE_NAME}"]`,
    )
    if (!existing) {
      const script = document.createElement('script')
      script.async = true
      script.src = `${MATOMO_URL}/matomo.js`
      script.type = 'text/plain'
      script.dataset.usercentrics = UC_SERVICE_NAME
      script.onerror = () => console.error(`Failed to load ${UC_SERVICE_NAME}`)
      document.head.appendChild(script)
    }
  }, [])

  useEffect(() => {
    if (!MATOMO_ENABLED) return

    const handleUcInitialized = () => applyConsent()

    const handleUcEvent = () => {
      setTimeout(applyConsent, 50)
    }

    globalThis.addEventListener('UC_UI_INITIALIZED', handleUcInitialized)
    globalThis.addEventListener('ucEvent', handleUcEvent)
    globalThis.addEventListener('UC_UI_CMP_EVENT', handleUcEvent)

    if (globalThis.UC_UI?.isInitialized?.()) {
      applyConsent()
    }

    return () => {
      globalThis.removeEventListener('UC_UI_INITIALIZED', handleUcInitialized)
      globalThis.removeEventListener('ucEvent', handleUcEvent)
      globalThis.removeEventListener('UC_UI_CMP_EVENT', handleUcEvent)
    }
  }, [applyConsent])

  useEffect(() => {
    if (!MATOMO_ENABLED || !globalThis._paq) return
    trackPageView()
  }, [location.pathname, trackPageView])

  return null
}

export default MatomoTracker
